import { useTranslation } from "react-i18next";
import { Reveal, Stagger, StaggerItem } from "../../../../components/motion";
import { cn } from "../../../../utils/classnames";
import { MEET_ASSIST_INQUIRY_PATH } from "../../meet-assist-inquiry/constants";
import { buildLocalizedPath } from "../navigation";
import { SERVICE_IMAGES } from "../serviceImages";
import { GoldCtaLink, RichText, ServiceHeroCard } from "./ui";


const ASSIST_TIERS = ["Standard", "Vip"];

/**
 * How each tier's card looks. VIP is set apart with a warm tint, a gold
 * title and a matching warm border and divider line. Standard keeps a transparent border so both cards have the same
 * 1px edge and their text lines up.
 */
const TIER_STYLES = {
  Standard: {
    cardClass: "tw:bg-[#F9F9F9] tw:border-transparent",
    titleClass: "tw:text-[#282828]",
    dividerClass: "tw:bg-[#F1F1F1]",
  },
  Vip: {
    cardClass: "tw:bg-[#F6F4F0] tw:border-[#E7E2D8]",
    titleClass: "tw:text-[#9A7B3F]",
    dividerClass: "tw:bg-[#E7E2D8]",
  },
};

/**
 * Card for one level of assistance: title, hairline, then what the staff do
 * on arrival and on departure. Both tiers share a row and stretch to the
 * taller one, as in the design.
 */
const AssistTier = ({ tierId }) => {
  const { t } = useTranslation();
  const { cardClass, titleClass, dividerClass } = TIER_STYLES[tierId];

  return (
    <div
      className={cn(
        "tw:h-full tw:rounded-[8px] tw:border tw:border-solid tw:p-6 tw:lg:p-9",
        cardClass
      )}
    >
      <div
        className={cn(
          "tw:font-hanken-grotesk tw:font-semibold tw:text-[18px] tw:leading-[25px]",
          titleClass
        )}
      >
        {t(`servicesPage.MeetGreet.${tierId}.Title`)}
      </div>
      <div
        className={cn(
          "tw:h-px tw:my-5 tw:lg:mt-[27px] tw:lg:mb-7",
          dividerClass
        )}
      />
      <RichText
        i18nKey={`servicesPage.MeetGreet.${tierId}.Description`}
        className="tw:font-hanken-grotesk tw:text-[#4E4E4E] tw:text-[14px] tw:lg:text-[15px] tw:leading-[20px] tw:lg:leading-[22px]"
        strongClassName="tw:text-[#9A7B3F]"
      />
    </div>
  );
};

export const MeetGreet = () => {
  const { t, i18n } = useTranslation();
  // Both "Inquire Now" buttons open the Meet & Assist inquiry form.
  const enquiryPath = buildLocalizedPath(i18n.language, MEET_ASSIST_INQUIRY_PATH);

  return (
    <div className="tw:px-5 tw:lg:px-[82px] tw:pt-6 tw:lg:pt-[72px] tw:pb-10 tw:lg:pb-[72px]">
      <ServiceHeroCard
        isFullWidth
        image={SERVICE_IMAGES.meetGreet.hero}
        eyebrow={t("servicesPage.MeetGreet.Kicker")}
        title={t("servicesPage.MeetGreet.Title")}
        description={
          <RichText
            i18nKey="servicesPage.MeetGreet.Intro"
            strongClassName="tw:text-[15px]"
          />
        }
        ctaLabel={t("servicesPage.MeetGreet.InquireButton")}
        ctaTo={enquiryPath}
      />

      <Stagger className="tw:mt-5 tw:lg:mt-6 tw:grid tw:grid-cols-1 tw:lg:grid-cols-2 tw:gap-4 tw:lg:gap-6">
        {ASSIST_TIERS.map((tierId) => (
          <StaggerItem key={tierId} className="tw:h-full">
            <AssistTier tierId={tierId} />
          </StaggerItem>
        ))}
      </Stagger>

      {/* VIP fine print: the title on the left, the notes in the same
          880px-wide column the Chauffeur tab uses for body copy. */}
      <Reveal className="tw:mt-4 tw:lg:mt-6 tw:bg-[#F9F9F9] tw:rounded-[8px] tw:p-6 tw:lg:p-8 tw:flex tw:flex-col tw:lg:flex-row tw:lg:justify-between tw:gap-4 tw:lg:gap-8">
        <div className="tw:font-hanken-grotesk">
          <div className="tw:font-normal tw:text-[13px] tw:leading-[19px] tw:text-[#8D8D8D]">
            {t("servicesPage.MeetGreet.Note.Kicker")}
          </div>
          <div className="tw:font-bold tw:text-[15px] tw:leading-[22px] tw:text-[#282828]">
            {t("servicesPage.MeetGreet.Note.Title")}
          </div>
        </div>
        <div className="tw:lg:w-[72.79%] tw:lg:shrink-0 tw:font-hanken-grotesk tw:text-[#4E4E4E] tw:text-[14px] tw:leading-[20px] tw:space-y-[3px]">
          <RichText i18nKey="servicesPage.MeetGreet.Note.Immigration" />
          <div>{t("servicesPage.MeetGreet.Note.Luggage")}</div>
        </div>
      </Reveal>

      <Reveal className="tw:mt-10 tw:lg:mt-[72px] tw:max-w-[551px] tw:mx-auto tw:text-center">
        <div className="tw:font-hanken-grotesk tw:text-[#4E4E4E] tw:text-[14px] tw:leading-[20px]">
          {t("servicesPage.MeetGreet.ClosingLine")}
        </div>
        <GoldCtaLink
          to={enquiryPath}
          label={t("servicesPage.MeetGreet.InquireButton")}
          className="tw:mt-[22px] tw:hover:-translate-y-[2px]"
        />
      </Reveal>
    </div>
  );
};
